import { createContext, useContext, useState, useEffect, useCallback, useMemo } from 'react';
import toast from 'react-hot-toast';
import { estaDisponible } from '../utils/disponibilidad';

const CART_STORAGE_KEY = 'cartItems';
const CART_VERSION_KEY = 'cartItems_v';
// Bumped to 2 when extras shape was introduced. Carts persisted under v1
// don't carry extras and would silently undercharge — better to wipe and
// let the user re-add than migrate an inferable shape.
const CART_VERSION = 2;
const CartContext = createContext(null);

const getProductId = (product) => product?._id || product?.id;
const getItemId = (item) => item?.product?._id || item?.product?.id;

const loadInitialItems = () => {
  try {
    const savedVersion = parseInt(localStorage.getItem(CART_VERSION_KEY), 10);
    if (savedVersion !== CART_VERSION) {
      localStorage.removeItem(CART_STORAGE_KEY);
      localStorage.setItem(CART_VERSION_KEY, String(CART_VERSION));
      return [];
    }
    const stored = localStorage.getItem(CART_STORAGE_KEY);
    const parsed = stored ? JSON.parse(stored) : [];
    return parsed.filter((i) => i.quantity > 0);
  } catch {
    return [];
  }
};

export function CartProvider({ children }) {
  const [items, setItems] = useState(loadInitialItems);
  // Item pendiente cuando addItem detecta conflicto de negocio. La UI global
  // (ReplaceCartConfirm) lee esto, muestra el modal, y llama confirm/cancel.
  const [pendingItem, setPendingItem] = useState(null);

  useEffect(() => {
    try {
      localStorage.setItem(CART_STORAGE_KEY, JSON.stringify(items));
      localStorage.setItem(CART_VERSION_KEY, String(CART_VERSION));
    } catch {}
  }, [items]);

  // Mutación pura de carrito + toast. Llamada tanto desde addItem (sin
  // conflicto) como desde confirmReplaceCart (después de vaciar).
  const _commitAdd = useCallback((product, quantity, extras) => {
    const productId = getProductId(product);
    const itemBusinessId = product?.businessId || null;

    setItems(prev => {
      const existing = prev.find(i => getItemId(i) === productId);
      if (existing) {
        return prev.map(i =>
          getItemId(i) === productId
            ? { ...i, quantity: i.quantity + quantity }
            : i
        );
      }
      return [...prev, {
        product: { ...product, businessId: itemBusinessId },
        quantity,
        businessId: itemBusinessId,
        extras,
      }];
    });

    toast.success('Agregado al carrito', {
      iconTheme: { primary: '#00ED9D', secondary: '#fff' }
    });
  }, []);

  const addItem = useCallback((product, quantity = 1, extras = []) => {
    const newBusinessId = product?.businessId || null;
    const currentBusinessId = items[0]?.businessId || null;

    // Conflicto: cart tiene items de otro negocio → guardar pendiente
    // para que ReplaceCartConfirm pida confirmación al usuario.
    if (items.length > 0 && currentBusinessId && newBusinessId && currentBusinessId !== newBusinessId) {
      setPendingItem({ product, quantity, extras });
      return;
    }

    _commitAdd(product, quantity, extras);
  }, [items, _commitAdd]);

  const confirmReplaceCart = useCallback(() => {
    if (!pendingItem) return;
    setItems([]);
    _commitAdd(pendingItem.product, pendingItem.quantity, pendingItem.extras);
    setPendingItem(null);
  }, [pendingItem, _commitAdd]);

  const cancelReplaceCart = useCallback(() => {
    setPendingItem(null);
  }, []);

  // Producto con extras esperando a que el usuario los elija. La UI global
  // (QuickAddOptions) abre la hoja de extras y al confirmar llama addItem.
  const [customizing, setCustomizing] = useState(null);

  // El "+" de las tarjetas. Sin extras agrega directo; con extras abre la hoja
  // para elegirlos (sin pasar por el detalle del producto), porque agregarlo
  // tal cual se saltaría las opciones obligatorias.
  const quickAdd = useCallback((product) => {
    // Los no disponibles se ven en el menú, pero no se pueden pedir.
    if (!estaDisponible(product)) return;
    if (product?.optionGroups?.length > 0) {
      setCustomizing(product);
      return;
    }
    addItem(product, 1);
  }, [addItem]);

  const closeCustomizing = useCallback(() => {
    setCustomizing(null);
  }, []);

  const removeItem = useCallback((productId, showToast = true) => {
    setItems(prev => prev.filter(i => getItemId(i) !== productId));
    if (showToast) {
      toast('Eliminado del carrito');
    }
  }, []);

  const updateQuantity = useCallback((productId, quantity) => {
    if (quantity <= 0) {
      setItems(prev => prev.filter(i => getItemId(i) !== productId));
      return;
    }
    setItems(prev =>
      prev.map(i =>
        getItemId(i) === productId ? { ...i, quantity } : i
      )
    );
  }, []);

  const incrementQuantity = useCallback((productId, amount = 1) => {
    setItems(prev =>
      prev.map(i =>
        getItemId(i) === productId
          ? { ...i, quantity: i.quantity + amount }
          : i
      )
    );
  }, []);

  const decrementQuantity = useCallback((productId, amount = 1, minQuantity = 1) => {
    setItems(prev => {
      const found = prev.find(i => getItemId(i) === productId);
      if (!found) return prev;
      const newQty = found.quantity - amount;

      if (minQuantity === 0 && newQty <= 0) {
        return prev.filter(i => getItemId(i) !== productId);
      }

      if (newQty < minQuantity) {
        return prev;
      }

      return prev.map(i =>
        getItemId(i) === productId ? { ...i, quantity: newQty } : i
      );
    });
  }, []);

  const clearCart = useCallback((showToast = false) => {
    setItems([]);
    if (showToast) toast('Carrito vaciado');
  }, []);

  const getItem = useCallback((productId) => {
    return items.find(i => getItemId(i) === productId);
  }, [items]);

  const isInCart = useCallback((productId) => {
    return items.some(i => getItemId(i) === productId);
  }, [items]);

  const subtotal = useMemo(() => {
    return items.reduce((sum, i) => {
      const productSubtotal = (i.product?.price || 0) * i.quantity;
      const extrasUnitSum = (i.extras || []).reduce(
        (s, ex) => s + (ex.price || 0) * (ex.quantity || 1),
        0
      );
      return sum + productSubtotal + extrasUnitSum * i.quantity;
    }, 0);
  }, [items]);

  const itemCount = useMemo(() => {
    return items.reduce((sum, i) => sum + i.quantity, 0);
  }, [items]);

  const isEmpty = items.length === 0;

  const value = {
    items,
    addItem,
    removeItem,
    updateQuantity,
    incrementQuantity,
    decrementQuantity,
    clearCart,
    getItem,
    isInCart,
    subtotal,
    itemCount,
    isEmpty,
    pendingItem,
    confirmReplaceCart,
    cancelReplaceCart,
    quickAdd,
    customizing,
    closeCustomizing,
  };

  return <CartContext.Provider value={value}>{children}</CartContext.Provider>;
}

export function useCart() {
  const ctx = useContext(CartContext);
  if (!ctx) {
    throw new Error('useCart debe usarse dentro de <CartProvider>');
  }
  return ctx;
}
